import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Menu, X, Heart, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import AboutModal from './AboutModal';
import { LiveVisitors } from './LiveVisitors';
import LanguageSwitcher from './LanguageSwitcher';
import { useT } from '../i18n/I18nProvider';

const NAV_ITEMS = [
  { label: 'About', labelKey: 'nav.about', kind: 'about' },
  {
    label: 'Projects',
    labelKey: 'nav.projects',
    section: 'programmes',
    children: [
      { label: 'Language For Junior', route: '/projects/language-for-junior' },
      { label: 'Civil Society Intervention Plus', route: '/projects/civil-society-intervention-plus' },
      { label: 'Counselling', route: '/projects/counselling' },
      { label: 'C3G', route: '/projects/c3g' },
      { label: 'Skill Training Initiative (STI)', route: '/projects/sti' },
      { label: 'Alumni', route: '/projects/alumni' },
      { label: 'Volunteer Induction Programme (VIP)', route: '/projects/vip' },
      { label: 'Medical Aid and Welfare', route: '/projects/medical-aid-welfare' },
    ],
  },
  {
    label: 'Research',
    labelKey: 'nav.research',
    route: '/projects/research',
  },
  {
    label: 'Branches',
    labelKey: 'nav.branches',
    route: '/branches',
  },
  {
    label: 'News & Events',
    labelKey: 'nav.news_events',
    section: 'events',
    children: [
      { label: 'HQ Events', labelKey: 'nav.hq_events', route: '/news/hq-events' },
      { label: 'Media Coverage', labelKey: 'nav.media_coverage', route: '/news/media-coverage' },
      { label: 'News Releases', labelKey: 'nav.news_releases', route: '/news/media-statement' },
      { label: 'News Letter', labelKey: 'nav.newsletter', route: '/news/newsletter' },
    ],
  },
  { label: 'Gallery', labelKey: 'nav.gallery', section: 'gallery' },
  { label: 'Videos', labelKey: 'nav.videos', section: 'videos' },
  { label: 'Careers', labelKey: 'nav.careers', section: 'careers' },
  {
    label: 'Contact',
    labelKey: 'nav.contact',
    section: 'contact',
    children: [
      { label: 'Contact Us', labelKey: 'nav.contact_us', section: 'contact' },
      { label: 'Room Booking', labelKey: 'nav.room_booking', route: '/room-booking' },
    ],
  },
];

// Per-item text color for a vibrant, on-brand navigation
const NAV_COLORS = {
  'About':         '#1565C0',
  'Projects':      '#1565C0',
  'Research':      '#1565C0',
  'Branches':      '#1565C0',
  'News & Events': '#1565C0',
  'Gallery':       '#1565C0',
  'Videos':        '#1565C0',
  'Careers':       '#1565C0',
  'Contact':       '#1565C0',
};

const Header = () => {
  const navigate = useNavigate();
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const closeTimer = useRef(null);

  const labelOf = (item) => (item.labelKey ? t(item.labelKey) : item.label);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id, hash) => {
    const target = (hash && document.getElementById(hash)) || document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'smooth' });
    if (hash) {
      // update URL hash without jumping
      window.history.replaceState(null, '', `#${hash}`);
    }
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleNavClick = (item) => {
    if (item.kind === 'about') {
      setAboutModalOpen(true);
      setMobileMenuOpen(false);
      setOpenDropdown(null);
    } else if (item.route) {
      setMobileMenuOpen(false);
      setOpenDropdown(null);
      navigate(item.route);
    } else if (item.section) {
      // If we're not on the home page, navigate to home + hash first
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => scrollToSection(item.section, item.hash), 200);
      } else {
        scrollToSection(item.section, item.hash);
      }
    }
  };

  const openMenu = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 180);
  };

  return (
    <>
      {/* Slim blue announcement strip — NGO reg + live visitors only */}
      <div className="bg-gradient-to-r from-[#0D47A1] via-[#1565C0] to-[#1976D2] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-white/90">
            <span className="font-semibold uppercase tracking-widest text-amber-300 hidden sm:inline">Est. 1979</span>
            <span className="hidden sm:inline text-white/40">•</span>
            <span>
              NGO Reg <span className="font-mono text-white/95">PPM-001-14-02011979</span>
            </span>
            <span className="hidden md:inline text-white/40">•</span>
            <span className="hidden md:inline">Tax-exempt 🇲🇾 Section 44(6)</span>
          </div>
          <LiveVisitors variant="inline" />
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-xl border-b border-blue-100'
            : 'bg-white border-b border-gray-100'
        }`}
        data-testid="main-header"
      >
        {/* Blue accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-[#0D47A1] via-[#1976D2] to-[#42A5F5]"></div>

        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <button
              onClick={() => {
                if (window.location.pathname !== '/') {
                  navigate('/');
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="flex items-center gap-3 group shrink-0"
              aria-label="EWRF home"
              data-testid="header-logo-btn"
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/ewrf_47years_logo.jpg`}
                alt="EWRF 47 Years Logo"
                className="h-32 md:h-40 w-auto max-w-none object-contain group-hover:scale-105 transition-transform"
              />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((item) => {
                const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                const isOpen = openDropdown === item.label;
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={hasChildren ? () => openMenu(item.label) : undefined}
                    onMouseLeave={hasChildren ? scheduleClose : undefined}
                  >
                    <button
                      onClick={() => handleNavClick(item)}
                      className="relative inline-flex items-center gap-1 px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors group"
                      style={{ color: NAV_COLORS[item.label] || '#0A1628' }}
                      data-testid={`nav-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
                      aria-haspopup={hasChildren ? 'menu' : undefined}
                      aria-expanded={hasChildren ? isOpen : undefined}
                    >
                      {labelOf(item)}
                      {hasChildren && (
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      )}
                      <span
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 group-hover:w-3/4 transition-all duration-300"
                        style={{ backgroundColor: NAV_COLORS[item.label] || '#1976D2' }}
                      ></span>
                    </button>

                    {hasChildren && (
                      <div
                        className={`absolute left-0 top-full pt-2 min-w-[240px] z-50 transition-all duration-200 ${
                          isOpen
                            ? 'opacity-100 translate-y-0 pointer-events-auto'
                            : 'opacity-0 -translate-y-1 pointer-events-none'
                        }`}
                        onMouseEnter={() => openMenu(item.label)}
                        onMouseLeave={scheduleClose}
                        data-testid={`dropdown-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <div className="bg-white rounded-xl shadow-2xl border border-blue-100 overflow-hidden ring-1 ring-black/5">
                          <div className="h-1 w-full bg-gradient-to-r from-[#0D47A1] via-[#1976D2] to-[#42A5F5]"></div>
                          <ul className="py-2" role="menu">
                            {item.children.map((sub) => (
                              <li key={sub.label} role="none">
                                <button
                                  role="menuitem"
                                  onClick={() => handleNavClick(sub)}
                                  className="w-full text-left px-4 py-2.5 text-sm font-semibold text-[#0A1628] hover:bg-blue-50 hover:text-[#1565C0] transition-colors flex items-center gap-2"
                                  data-testid={`dropdown-item-${sub.label.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#1976D2] to-[#42A5F5]"></span>
                                  {labelOf(sub)}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <button
                className="p-2 text-[#0A1628] hover:text-[#1565C0] transition-colors hidden md:block"
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </button>
              <Button
                onClick={() => scrollToSection('donate')}
                className="bg-gradient-to-r from-[#0D47A1] via-[#1565C0] to-[#1976D2] hover:from-[#0A3D91] hover:via-[#0D5BAF] hover:to-[#1565C0] text-white px-5 md:px-7 py-5 rounded-full font-black uppercase tracking-wider text-xs md:text-sm shadow-2xl hover:shadow-blue-500/50 hover:scale-105 transition-all"
                data-testid="header-donate-btn"
              >
                <Heart className="w-4 h-4 mr-1.5 fill-white" />
                {t('footer.donate')}
              </Button>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-[#0A1628] hover:text-[#1565C0] transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <nav className="lg:hidden pb-4 pt-2 border-t border-gray-100">
              <div className="flex flex-col gap-1 mt-2">
                {NAV_ITEMS.map((item) => {
                  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                  const expanded = !!mobileExpanded[item.label];
                  return (
                    <div key={item.label}>
                      <div className="flex items-stretch">
                        <button
                          onClick={() => handleNavClick(item)}
                          className="flex-1 text-left px-3 py-3 rounded-lg text-sm font-bold uppercase tracking-wider hover:bg-blue-50 transition-colors"
                          style={{ color: NAV_COLORS[item.label] || '#0A1628' }}
                        >
                          {labelOf(item)}
                        </button>
                        {hasChildren && (
                          <button
                            onClick={() =>
                              setMobileExpanded((prev) => ({ ...prev, [item.label]: !prev[item.label] }))
                            }
                            className="px-3 text-[#0A1628] hover:text-[#1565C0]"
                            aria-label={`Toggle ${item.label} submenu`}
                            data-testid={`mobile-toggle-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <ChevronDown
                              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                            />
                          </button>
                        )}
                      </div>
                      {hasChildren && expanded && (
                        <div className="ml-3 mt-1 mb-2 border-l-2 border-blue-100 pl-3 flex flex-col gap-0.5">
                          {item.children.map((sub) => (
                            <button
                              key={sub.label}
                              onClick={() => handleNavClick(sub)}
                              className="text-left px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider text-[#0A1628]/80 hover:bg-blue-50 hover:text-[#1565C0] transition-colors"
                              data-testid={`mobile-dropdown-item-${sub.label.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              {labelOf(sub)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </nav>
          )}
        </div>

        <AboutModal isOpen={aboutModalOpen} onClose={() => setAboutModalOpen(false)} />
      </header>
    </>
  );
};

export default Header;
